import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, Eye } from 'lucide-react';

import { useProjects, useDeleteProject } from '@/hooks/useProjects';
import { getErrorMessage } from '@/utils/error';
import { resolveImageUrl } from '@/utils/url';

export function ProjectsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const pageSize = 4;
  const { data, isLoading, error } = useProjects(page, pageSize);
  const deleteProject = useDeleteProject();

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete project "${name}"?`)) return;
    try {
      await deleteProject.mutateAsync(id);
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-dark-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-error">Failed to load projects: {getErrorMessage(error)}</p>
      </div>
    );
  }

  const projects = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="flex flex-1 flex-col p-8">
      {/* Page Header */}
      <div className="mb-12 flex items-end justify-between">
        <div className="max-w-2xl">
          <h2 className="mb-4 font-headline text-5xl font-extrabold tracking-tighter text-dark-on-surface">
            Project Inventory
          </h2>
          <p className="text-lg leading-relaxed text-dark-on-surface-variant">
            Manage and deploy architectural assets. All technical specifications and
            design protocols are unified within the Precision Engine architecture.
          </p>
        </div>
        <div>
          <button
            onClick={() => navigate('/dashboard/create_project')}
            className="flex cursor-pointer items-center gap-3 rounded-full bg-gradient-to-r from-dark-primary-container to-dark-secondary-container px-8 py-3 text-sm font-bold text-white shadow-[0_40px_40px_-10px_rgba(0,26,64,0.4)] transition-transform duration-300 hover:scale-105"
          >
            <Plus className="h-5 w-5" strokeWidth={2.5} />
            Create New Project
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="space-y-4">
        {/* Table Headers */}
        <div className="grid grid-cols-12 px-6 py-2">
          <div className="col-span-6">
            <span className="text-[10px] uppercase tracking-widest text-dark-outline">
              Project Detail & Identifier
            </span>
          </div>
          <div className="col-span-2 text-center">
            <span className="text-[10px] uppercase tracking-widest text-dark-outline">
              Technical Status
            </span>
          </div>
          <div className="col-span-2 text-center">
            <span className="text-[10px] uppercase tracking-widest text-dark-outline">
              Revision Date
            </span>
          </div>
          <div className="col-span-2 text-right">
            <span className="text-[10px] uppercase tracking-widest text-dark-outline">
              Actions
            </span>
          </div>
        </div>

        {projects.length === 0 && (
          <div className="py-16 text-center text-dark-on-surface-variant">
            No projects yet. Create your first project to get started.
          </div>
        )}

        {/* Project Rows */}
        {projects.map((project) => (
          <div
            key={project.id}
            className="group grid cursor-pointer grid-cols-12 items-center rounded-xl bg-dark-surface-container-low px-6 py-4 transition-colors hover:bg-dark-surface-container"
            onClick={() => navigate(`/dashboard/projects/${project.id}/edit`)}
          >
            <div className="col-span-6 flex items-center gap-6">
              <div className="h-14 w-20 overflow-hidden rounded-lg bg-dark-surface-container-highest">
                {project.thumbnailUrl ? (
                  <img
                    src={resolveImageUrl(project.thumbnailUrl) || ''}
                    alt={project.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs font-bold text-dark-outline">
                    IMG
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-headline text-lg font-bold text-dark-on-surface transition-colors group-hover:text-dark-secondary">
                  {project.name}
                </h3>
                <p className="text-xs tracking-tight text-dark-outline">
                  {project.code || `PRJ-${project.id}`}
                </p>
              </div>
            </div>
            <div className="col-span-2 flex justify-center">
              <span className="rounded-full bg-dark-surface-container-highest px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-dark-secondary">
                {project.status}
              </span>
            </div>
            <div className="col-span-2 text-center">
              <span className="text-sm text-dark-on-surface-variant">
                {project.updatedDate ? formatDate(project.updatedDate) : '---'}
              </span>
            </div>
            <div className="col-span-2 flex justify-end gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); window.open(`${import.meta.env.BASE_URL}project/${project.id}`, '_blank'); }}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-dark-surface-container-highest text-dark-on-surface transition-all hover:bg-dark-tertiary-container hover:text-white"
                title="View project"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/projects/${project.id}/edit`); }}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-dark-surface-container-highest text-dark-on-surface transition-all hover:bg-dark-primary-container hover:text-white"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(project.id, project.name); }}
                disabled={deleteProject.isPending}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-dark-surface-container-highest text-dark-on-surface transition-all hover:bg-[#93000a] hover:text-white disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="mt-12 flex items-center justify-between text-[10px] uppercase tracking-widest text-dark-outline">
        <p>
          Displaying {projects.length} of {totalElements} active projects
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="transition-colors hover:text-dark-secondary disabled:opacity-30"
          >
            Previous Page
          </button>
          <span className="text-dark-on-surface">
            Page {String(page + 1).padStart(2, '0')} of{' '}
            {String(totalPages).padStart(2, '0')}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="transition-colors hover:text-dark-secondary disabled:opacity-30"
          >
            Next Page
          </button>
        </div>
      </div>
    </div>
  );
}
